import type { YMarketOrder } from './types'

const CACHE_PREFIX = 'almaz:orders-cache:v1:'
const MAX_AGE_MS = 6 * 60 * 60 * 1000

/**
 * The browser half of the freshness contract.
 *
 * The server refuses to let anything hold an order response, and joins the
 * durable PostgreSQL status onto every page it serves. This cache exists only
 * so a returning tab paints something immediately, so a render older than the
 * server's own sixty-second provider window is shown as saved, not as current.
 * A cache entry is never allowed to look like a live answer.
 */
export const ORDERS_CACHE_FRESH_MS = 60 * 1000
export const ORDERS_CACHE_MAX_AGE_MS = MAX_AGE_MS
export const ORDERS_STALE_CACHE_NOTICE = 'Показаны сохранённые данные · обновляем'

/** The operator sentence for a list the server served stale after Yandex failed. */
export function ordersMarketStaleNotice(observedAt: string | null): string {
  const parsed = observedAt === null ? Number.NaN : Date.parse(observedAt)
  const time = Number.isFinite(parsed)
    ? new Date(parsed).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : 'неизвестное время'
  return `Данные Маркета устарели: показан список на ${time}. Обновим автоматически.`
}

interface CachedOrders {
  savedAt: number
  orders: YMarketOrder[]
  nextPageToken: string | null
}

export interface LoadedOrdersCache extends CachedOrders {
  /** True once the entry is older than the server's own freshness window. */
  stale: boolean
}

export function loadOrdersCache(statuses: string, now = Date.now()): LoadedOrdersCache | null {
  if (typeof window === 'undefined') return null
  try {
    const cached = JSON.parse(sessionStorage.getItem(`${CACHE_PREFIX}${statuses}`) || 'null') as CachedOrders | null
    if (!cached || !Array.isArray(cached.orders) || now - cached.savedAt > MAX_AGE_MS) return null
    return { ...cached, stale: now - cached.savedAt > ORDERS_CACHE_FRESH_MS }
  } catch {
    return null
  }
}

export function saveOrdersCache(statuses: string, orders: YMarketOrder[], nextPageToken: string | null): void {
  if (typeof window === 'undefined') return
  try {
    const storedOrders = orders.slice(0, 250)
    // A truncated cache cannot safely resume from the provider token that
    // follows the complete in-memory list: doing so would leave a permanent
    // gap after hydration.
    const storedToken = storedOrders.length === orders.length ? nextPageToken : null
    sessionStorage.setItem(`${CACHE_PREFIX}${statuses}`, JSON.stringify({
      savedAt: Date.now(), orders: storedOrders, nextPageToken: storedToken,
    } satisfies CachedOrders))
  } catch {
    // A cache failure must never block the live order list.
  }
}
